import { Buffer, GL_ARRAY_BUFFER, GL_BYTE, GL_ELEMENT_ARRAY_BUFFER, GL_FLOAT, GL_INT, GL_SHORT, GL_UNSIGNED_BYTE, GL_UNSIGNED_INT, GL_UNSIGNED_SHORT, } from "./buffer";
export const GL_POINTS = 0x0000;
export const GL_LINES = 0x0001;
export const GL_LINE_LOOP = 0x0002;
export const GL_LINE_STRIP = 0x0003;
export const GL_TRIANGLES = 0x0004;
export const GL_TRIANGLE_STRIP = 0x0005;
export const GL_TRIANGLE_FAN = 0x0006;
let v = 1;
export const PRIMITIVE_MAP = {
    triangles: GL_TRIANGLES,
    points: GL_POINTS,
    lines: GL_LINES,
    line_loop: GL_LINE_LOOP,
    line_strip: GL_LINE_STRIP,
    triangle_strip: GL_TRIANGLE_STRIP,
    triangle_fan: GL_TRIANGLE_FAN,
};
export class VAO {
    constructor(synth, props = {}) {
        this.synth = synth;
        this.name = props.name || "vao_" + v++;
        this.attributes = {};
        if (props.attrs)
            this.attrs(props.attrs);
        if (props.cells)
            this.cells(props.cells);
        this._elements = props.elements;
        this._count = props.count;
        this._rasterizerDiscard = props.rasterizerDiscard ?? false;
        this._primitive =
            typeof props.primitive === "string" ? PRIMITIVE_MAP[props.primitive] : props.primitive ?? GL_POINTS;
        this.buffer = this.synth.renderer.gl.createVertexArray();
    }
    //////////////////////
    //////////////////////
    hasAttr(name) {
        return !!this.attributes[name];
    }
    attr(name, attr) {
        this.attributes[name] =
            attr instanceof Buffer ? attr : new Buffer(this.synth, { name: this.name + "_" + name, ...attr });
        return this;
    }
    attrs(attrs) {
        for (const [name, attr] of Object.entries(attrs)) {
            if (this.attributes[name])
                this.attributes[name].clear();
            this.attributes[name] =
                attr instanceof Buffer ? attr : new Buffer(this.synth, { name: this.name + "_" + name, ...attr });
        }
        return this;
    }
    deleteAttr(name, deleteBuffer = false) {
        if (this.attributes[name]) {
            if (deleteBuffer)
                this.attributes[name].clear();
            delete this.attributes[name];
        }
        return this;
    }
    cells(cells) {
        if (this.indices)
            this.indices.clear(); // Pulisce il buffer precedente se esiste
        this.indices =
            cells instanceof Buffer
                ? cells
                : new Buffer(this.synth, {
                    ...(Array.isArray(cells) ? { data: Uint16Array.from(cells) } : cells),
                    target: GL_ELEMENT_ARRAY_BUFFER,
                    name: this.name + "_cells",
                });
        return this;
    }
    elements(elements) {
        this._elements = elements;
        return this;
    }
    count(count) {
        this._count = count;
        return this;
    }
    primitive(primitive) {
        this._primitive = primitive;
        return this;
    }
    rasterizerDiscard(rasterizerDiscard = true) {
        this._rasterizerDiscard = rasterizerDiscard;
        return this;
    }
    instances(instances = 0) {
        this._instances = instances;
        if (this._instances > 0) {
            this.attrs({
                instance: {
                    data: new Uint32Array([...Array(instances).keys()]),
                    size: 1,
                    divisor: 1,
                    type: GL_UNSIGNED_INT,
                    target: GL_ARRAY_BUFFER,
                },
                instanceOffset: {
                    data: new Float32Array([...Array(instances).keys()].map(i => i / instances)),
                    size: 1,
                    divisor: 1,
                    type: GL_FLOAT,
                    target: GL_ARRAY_BUFFER,
                },
            });
        }
        else {
            delete this.attributes["instance"];
            delete this.attributes["instanceOffset"];
        }
        return this;
    }
    //////////////////////
    bindVertexAttrs(shader) {
        if (!shader || !shader.program) {
            console.warn("shader not found or not compiled");
            return this;
        }
        const gl = this.synth.renderer.gl;
        gl.bindVertexArray(this.buffer);
        for (let name in this.attributes) {
            const attr = this.attributes[name];
            const location = gl.getAttribLocation(shader.program, name);
            if (location !== -1) {
                gl.bindBuffer(attr._target, attr._buffer);
                gl.enableVertexAttribArray(location);
                if (attr._type === GL_INT ||
                    attr._type === GL_UNSIGNED_INT ||
                    attr._type === GL_SHORT ||
                    attr._type === GL_UNSIGNED_SHORT ||
                    attr._type === GL_BYTE ||
                    attr._type === GL_UNSIGNED_BYTE) {
                    gl.vertexAttribIPointer(location, attr._size, attr._type, attr._stride, attr._offset);
                }
                else {
                    gl.vertexAttribPointer(location, attr._size, attr._type, false, attr._stride, attr._offset);
                }
                gl.vertexAttribDivisor(location, attr._divisor);
            }
            else {
                console.warn(`location ${name} not found`);
            }
        }
        if (this.indices) {
            gl.bindBuffer(this.indices._target, this.indices._buffer);
        }
        gl.bindVertexArray(null);
        return this;
    }
    clear() {
        // destroy all buffers
        Object.keys(this.attributes).forEach(name => this.deleteAttr(name, true));
        if (this.indices) {
            this.indices.clear();
            this.indices = undefined;
        }
    }
    clone() {
        const clonedAttrs = {};
        for (const name in this.attributes) {
            clonedAttrs[name] = this.attributes[name].clone();
        }
        const clonedCells = this.indices ? this.indices.clone() : undefined;
        const vao = new VAO(this.synth, {
            name: this.name + "_cloned",
            attrs: clonedAttrs,
            cells: clonedCells,
            elements: this._elements,
            count: this._count,
            primitive: this._primitive,
            instances: this._instances,
            rasterizerDiscard: this._rasterizerDiscard,
        });
        return vao;
    }
}
